import {apiClient, apiStreamQuery} from "../connection/BlinkApiClient";
import {Money} from "../../stem-core/localization/Money";
import {RecurringPaymentStoreObject} from "./RecurringPaymentStoreObject";
import {SubscriptionDiscount} from "./SubscriptionDiscountStore";
import {field} from "../../stem-core/state/StoreField";
import {MerchantUser} from "./merchant/MerchantUserStore";
import {SubscriptionBenefit} from "./SubscriptionBenefitStore";
import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {UserProfile} from "./UserProfileStore";
import {BillingPlan, SubscriptionOffer} from "./SubscriptionOfferStore";
import {SubscriptionCoverage} from "./SubscriptionCoverageStore";
import {StoreId} from "../../stem-core/state/State";
import {UserAddress} from "./UserAddressStore";

export const SubscriptionApplyPolicy = {
    INSTANTLY: "instantly",
    NEXT_BILLING_CYCLE: "next_billing_cycle",
};


@globalStore
export class Subscription extends BaseStore(
    "Subscription",
    {dependencies: ["Currency", "Merchant", "SubscriptionCoverage", "SubscriptionOffer", "BillingPlan"]},
    RecurringPaymentStoreObject
) {
    @field("BillingPlan") billingPlan: BillingPlan;
    @field("SubscriptionCoverage") coverage: SubscriptionCoverage;
    @field("SubscriptionOffer") recurringOffer: SubscriptionOffer;
    @field("SubscriptionDiscount") discount: SubscriptionDiscount;
    @field("Subscription") fromGroupSubscription: Subscription;
    @field(Number) userId: number;
    @field("UserProfile") user: UserProfile;
    @field("UserProfile") billedUser: UserProfile;
    // TODO @Mihai Only for group subscriptions, disabled for now
    @field(Boolean) isGroupSubscription: boolean;
    @field(Array) groupEmailDomains: string[];
    @field(Array) groupIpV4Ranges: string[];
    @field(Number) groupNumCustomBeneficiaries: number;
    @field(Boolean) ownerCanEditIpRanges: boolean;
    @field(Boolean) ownerCanEditDomains: boolean;
    @field(String) blinkSignature: string;
    @field(Array) extraBenefitIds: number[];

    getAllBenefitIds(): number[] {
        const benefitIds = new Set<number>([...this.coverage.benefitIds, ...(this.extraBenefitIds || [])]);
        if (this.recurringOffer) {
            for (const id of this.recurringOffer.extraBenefitIds) {
                benefitIds.add(id);
            }
        }
        return Array.from(benefitIds);
    }

    async reactivate(): Promise<any> {
       return apiReactivateSubscription(this.id);
    }

    async updatePaymentMethod(paymentMethodId: number): Promise<any> {
        return apiUpdateSubscription({
            subscriptionId: this.id,
            preferredPaymentMethodId: paymentMethodId,
        });
    }

    getMerchantUser(): MerchantUser | null {
        return MerchantUser.getByUserAndMerchant(this.userId, this.merchantId);
    }

    // TODO Deprecate this field
    getDiscount(): SubscriptionDiscount | null {
        return this.discount;
    }

    getPrice(): Money | null {
        return Money.optionally(this.lastCyclePrice?.finalPrice, this.getCurrency());
    }

    serializeForSDK(): any {
        // TODO migrate `amount` + `currencyCode` to Money.toRichJSON() once merchants have been notified.
        const {id, deliveryAddressId, blinkSignature, nextPaymentAttempt, userId, amount, createdAt, canceledAt, activeUntil, billingPlanId} = this;
        let obj = {id, deliveryAddressId, blinkSignature, nextPaymentAttempt, userId, amount, createdAt, canceledAt, activeUntil, billingPlanId};
        obj.currencyCode = this.getCurrency().getIsoCode();
        obj.name = this.coverage.getName();
        obj.billingPlan = this.billingPlan.serializeForSDK();

        const benefitIds = this.getAllBenefitIds();
        obj.benefits = benefitIds.map((benefitId) => {
            const benefit = SubscriptionBenefit.get(benefitId);
            if (!benefit) {
                return {
                    id: benefitId,
                }
            }
            return benefit.serializeForSDK();
        });
        return obj;
    }

    formatPriceShortened(): string {
        return `${this.getPrice()}/${this.billingPlan.getAbbreviatedCycleDuration()}`;
    }

    formatPrice(): string {
        return `${this.getPrice()}/${this.billingPlan.getCycleDuration(true)}`;
    }

    static getActive(): Subscription[] {
        return this.filter(subscription => subscription.isActive());
    }

    static getUnpaid(): Subscription[] {
        return this.filter(subscription => subscription.isUnpaid());
    }

    static getActiveAndNotCanceled(): Subscription[] {
        return this.filter(subscription => subscription.status === this.Status.ACTIVE);
    }

    static getPast(): Subscription[] {
        return this.filter(subscription => !subscription.isActive() && !subscription.isUnpaid());
    }

    static getActiveWithAddress(address: UserAddress): Subscription[] {
        return this.getActive().filter(sub => sub.getShippingAddress() === address);
    }
}

export async function apiSubscribe(payload: any): Promise<Subscription | undefined> {
    const response = await apiClient.post("/subscriptions/subscribe", payload);
    return Subscription.load(response).find(subscription => subscription.isActive());
}

export async function apiUpdateSubscription(payload: any): Promise<Subscription | undefined> {
    const response = await apiClient.post("/subscriptions/update_subscription", payload);
    return Subscription.load(response).find(subscription => !subscription.isFinished());
}

export async function apiRequestSubscriptionPriceOnAddressChange(subscriptionId: number, merchantId: number, deliveryAddressId: number): Promise<any> {
    return apiClient.get("/subscriptions/calculate_update_subscription_price", {
        subscriptionId,
        merchantId,
        deliveryAddressId,
    });
}

export async function apiCancelSubscription(subscriptionId: StoreId): Promise<any> {
    return apiClient.post("/subscriptions/cancel", {subscriptionId});
}

export async function apiReactivateSubscription(subscriptionId: StoreId): Promise<any> {
    return apiClient.post("/subscriptions/reactivate", {subscriptionId});
}

export async function apiMerchantUpdateSubscription(request: any): Promise<any> {
    return apiClient.post("/merchant/update_subscription", request);
}

export async function apiMerchantCancelSubscription(request: any): Promise<any> {
    return apiClient.post("/merchant/cancel_subscription", request);
}

export async function apiMerchantCreateGroupSubscription(request: any): Promise<Subscription> {
    const response = await apiClient.post("/merchant/create_group_subscription", request);
    return Subscription.loadObject(response);
}

export async function apiMerchantEditGroupSubscription(request: any): Promise<any> {
    return apiClient.post("/merchant/edit_group_subscription", request);
}

export async function apiMerchantProcessSubscriptionSentToOffline(request: any): Promise<any> {
    return apiClient.post("/external_providers/process_subscription_sent_to_offline", request);
}

export function apiStreamSubscriptions(filters: Record<string, any>): any {
    return apiStreamQuery("/merchant/get_subscriptions", filters);
}
